'use client';

import clsx from 'clsx';
import { FC } from 'react';
import { Button } from 'atoms/Button';
import { Image } from 'atoms/Image/Image';
import { P } from 'atoms/Typography';
import { Modal } from 'molecules/Modal';
import { SizeSelectBare } from 'organisms/ProductPriceSection/SizeSelect';
import { useTranslations } from 'modules/i18n/useTranslations';
import { useMediaQuery } from 'modules/mui/hooks';
import { useCart } from 'modules/shop/cart/hooks';
import giftCardImagesManager from 'modules/shop/giftCardImagesManager';
import { useSiteConfig } from 'modules/shop/siteConfig/hooks';
import { IGiftCardTile, IGiftCardTileContainer } from '../types';
import { useGiftCardTile } from './useGiftCardTile';
import styles from './styles.module.css';

export const GiftCardTile: FC<IGiftCardTile> = ({
  product,
  modal,
  select,
  handleAddToCart,
  isDisabled,
}) => {
  const t = useTranslations();
  const { isMobile } = useMediaQuery();

  const { name, mainImage, relevantImage, currency } = product;
  const { sizeOptions, selectedVariant, setSelectedVariant } = select;
  const { openModal, toggleModal } = modal;

  return (
    <div className={styles.tile}>
      {isMobile && <h2 className={styles.title}>{name}</h2>}
      {mainImage && <Image src={mainImage?.filename} alt={mainImage?.alt} />}
      <div className={styles.info}>
        {!isMobile && <h2 className={styles.title}>{name}</h2>}
        <div className={styles.tileFooter}>
          <div className={styles.selectWrapper}>
            <SizeSelectBare
              label={t('common.fields.desired-amount')}
              options={sizeOptions}
              value={selectedVariant}
              onChange={(e) => setSelectedVariant(e.target.value)}
              classes={{ root: styles.selectRoot, select: styles.select }}
            />
            <span className={styles.currency}>{currency}</span>
          </div>
          <Button
            onClick={toggleModal}
            disabled={isDisabled || !relevantImage}
            className={styles.button}
          >
            {t('common.buttons.preview')}
          </Button>
          <Button
            onClick={handleAddToCart}
            disabled={isDisabled}
            className={clsx(styles.button, styles.addToCartBtn)}
          >
            {t('common.buttons.buy')}
          </Button>
          <P className={styles.p}>{t('gift-card.vat-info')}</P>
        </div>
      </div>

      {relevantImage && (
        <Modal
          open={openModal}
          onClose={toggleModal}
          className={styles.modal}
          classes={{
            container: styles.modalContainer,
          }}
        >
          <Image src={relevantImage.filename} alt={relevantImage.alt} />
        </Modal>
      )}
    </div>
  );
};

export const GiftCardTileContainer: FC<IGiftCardTileContainer> = ({
  product,
}) => {
  const { gtmData } = useSiteConfig();
  const { addToCart } = useCart();
  const {
    product: { currency, relevantImage },
    modal,
    select,
    handleAddToCart,
    isDisabled,
  } = useGiftCardTile({
    product,
    onAddToCart: (id: string, q: number, limit?: number) => {
      addToCart(id, q, gtmData?.pageType, limit);
    },
    onAddStorageGiftCardImage: giftCardImagesManager.addStorageGiftCardImage,
  });

  return (
    <GiftCardTile
      product={{
        name: product.name,
        mainImage: product.mainImage,
        currency,
        relevantImage,
      }}
      select={select}
      modal={modal}
      handleAddToCart={handleAddToCart}
      isDisabled={isDisabled}
    />
  );
};
